import {GiftedWishForm} from "@/components/Wish/GiftedWishForm.tsx";
import {AuthProfile, Wish, Wishlist} from "@/api/types.ts";
import {useOutletContext, useParams} from "react-router-dom";
import {useWishlist} from "@/providers/WishlistProvider.tsx";
import {Caption, Headline} from "@telegram-apps/telegram-ui";
import {useNavigation} from "@/providers/NavigationProvider.tsx";
import {useAuthProfile} from "@/providers/AuthProfileProvider.tsx";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {GiftedWishPreview} from "@/components/Wish/GiftedWishPreview.tsx";
import {WishOutletContext} from "@/pages/layout/WishOutletContext.ts";

export const GiftedWishPage = () => {
  const {wishId, wishlistId} = useParams();
  const {navigateToRoute, navigateBack} = useNavigation();
  const {t} = useTranslation();
  const [showGiftedBy, setShowGiftedBy] = useState<boolean>(false);
  const [draftGiftedComment, setDraftGiftedComment] = useState<string>("");

  let onWishSaved: (() => Promise<void>) | undefined = undefined;
  let refreshWishlist: (() => Promise<void>) | undefined = undefined;
  let wishFromContext: Wish | undefined = undefined;
  let setWishFromContext: ((wish: Wish | undefined) => void) | undefined = undefined;
  try {
    const outletContext = useOutletContext<WishOutletContext & {refreshWishlist?: () => Promise<void>}>();
    onWishSaved = outletContext.onWishSaved;
    refreshWishlist = outletContext.refreshWishlist;
    wishFromContext = outletContext.wish;
    setWishFromContext = outletContext.setWish;
  } catch {
    onWishSaved = undefined;
    refreshWishlist = undefined;
    wishFromContext = undefined;
    setWishFromContext = undefined;
  }

  let wishlistFromContext: Wishlist | undefined = undefined;
  let setWishlistFromContext: (wishlist: Wishlist | undefined) => void = () => {};
  try {
    const {wishlist, setWishlist} = useWishlist();
    wishlistFromContext = wishlist;
    setWishlistFromContext = setWishlist;
  } catch {
    setWishlistFromContext(undefined);
  }

  let profileFromContext: AuthProfile | undefined = undefined;
  try {
    const {authProfile} = useAuthProfile();
    profileFromContext = authProfile;
  } catch {
    profileFromContext = undefined;
  }

  let contextWish: Wish | undefined = undefined;
  if (wishId) {
    contextWish = wishlistFromContext?.wishes.find(wish => wish.id === wishId);

    if (!contextWish && profileFromContext) {
      contextWish = profileFromContext.allWishes.find(wish => wish.id === wishId);
    }
  }

  const wish = contextWish || wishFromContext;
  const canShowGiftedBy = Boolean(wish?.bookedBy && wish.bookedByUser);

  useEffect(() => {
    setShowGiftedBy(canShowGiftedBy && Boolean(wish?.showGiftedBy));
  }, [canShowGiftedBy, wish?.id, wish?.showGiftedBy]);

  useEffect(() => {
    setDraftGiftedComment(wish?.giftedComment || "");
  }, [wish?.giftedComment, wish?.id]);

  const onSaved = async (updatedWish: Wish) => {
    if (wishlistFromContext) {
      const wishes = updatedWish.status === "gifted"
        ? wishlistFromContext.wishes.filter(wish => wish.id !== updatedWish.id)
        : wishlistFromContext.wishes.map(wish => wish.id === updatedWish.id ? updatedWish : wish);
      setWishlistFromContext({...wishlistFromContext, wishes});
    }

    if (onWishSaved) {
      await onWishSaved();
    }
    setWishFromContext?.(updatedWish);
    if (refreshWishlist) {
      await refreshWishlist();
    }

    const returnWishlistId = wishlistFromContext?.id || wishlistId;
    if (returnWishlistId) {
      navigateToRoute(`/main/wishlists/${returnWishlistId}`, true);
      return;
    }

    navigateBack(true);
  };

  if (!wish) {
    return <Caption className="text-color-hint">{t("wish.gifted.notLoaded")}</Caption>;
  }

  const pageClassName = `mt-3 pb-10 ${wishlistId ? "" : "mx-3"}`;
  const previewWish = {...wish, giftedComment: draftGiftedComment || null};
  const editWishRoute = wishlistId
    ? `/main/wishlists/${wishlistId}/wishes/${wish.id}/edit`
    : `/main/wishes/gifted/${wish.id}/edit`;

  return (
    <div className={pageClassName}>
      <Headline className="text-center mb-3" weight="2">{t("wish.gifted.action")}</Headline>
      <div className="gifted-wish-preview-block">
        <GiftedWishPreview
          wish={previewWish}
          showGiftedBy={showGiftedBy}
          readonly={true}
          onEditClick={() => navigateToRoute(editWishRoute)}
        />
      </div>
      <GiftedWishForm
        wish={wish}
        onSaved={onSaved}
        showGiftedBy={showGiftedBy}
        onShowGiftedByChange={setShowGiftedBy}
        onGiftedCommentChange={setDraftGiftedComment}
      />
    </div>
  );
};
